import { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Button } from '@artstorefronts/ui';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import ProfileSettings from '../../../components/Settings/ProfileSettings';
import { ADMIN_PROFILE_QUERY } from '../../../graphql/queries';
import { ADMIN_UPDATE_PROFILE_COMPREHENSIVE, ADMIN_UPDATE_USER } from '../../../graphql/mutations';
import { USERNAME_ONLY_PLATFORMS, prepareSocialLinkForApi } from '../../../utils/socialLinks';
import { locationPayload } from '@arthelper/shared/profile-location';

export default function ProfileEditPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const { data, loading, error, refetch } = useQuery(ADMIN_PROFILE_QUERY, {
    variables: { profileId: id },
    fetchPolicy: 'cache-and-network',
  });

  const [adminUpdateProfileComprehensive] = useMutation(ADMIN_UPDATE_PROFILE_COMPREHENSIVE);
  const [adminUpdateUser] = useMutation(ADMIN_UPDATE_USER);

  const profile = data?.adminProfile;
  // The form seeds once, from the first load, and ignores later refetches — so
  // the location diff must be against that same snapshot, not a newer `profile`.
  const seedProfile = useRef<any>(null);
  if (profile && !seedProfile.current) {
    seedProfile.current = profile;
  }

  const handleFormChange = (isDirty: boolean, hasErrors: boolean) => {
    setHasChanges(isDirty);
    setHasErrors(hasErrors);
  };

  const handleSave = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const handleProfileSettingsSave = async (values: any) => {
    if (!profile) return;

    setIsSaving(true);

    try {
      // Filter out user fields that aren't part of UpdateAdminProfileComprehensiveInput
      const { firstName, lastName, story, ...formFields } = values;

      const profileFields = locationPayload(formFields, seedProfile.current ?? profile);

      // Clean social links to remove frontend-only fields and construct URLs from usernames

      const cleanedSocialLinks = (formFields.socialLinks || [])
        .filter((link: any) => {
          if (!link.type) return false;
          const isUsernameOnly = USERNAME_ONLY_PLATFORMS.has(link.type);
          if (isUsernameOnly) {
            return link.username && link.username.trim().length > 0;
          } else {
            return link.url && link.url.trim().length > 0;
          }
        })
        .map(prepareSocialLinkForApi);

      // Update user fields if they have changed
      const userFieldsChanged =
        firstName !== profile.user?.firstName || lastName !== profile.user?.lastName || story !== profile.user?.story;

      if (userFieldsChanged) {
        await adminUpdateUser({
          variables: {
            input: {
              asfId: profile.user?.user_id || profile.user?.asfId,
              firstName: firstName || null,
              lastName: lastName || null,
              story: story || null,
            },
          },
        });
      }

      // Update profile fields
      await adminUpdateProfileComprehensive({
        variables: {
          input: {
            profileId: profile.id,
            ...profileFields,
            socialLinks: cleanedSocialLinks,
          },
        },
      });
      toast.success(t('profiles.toast.updated'));
      navigate('/admin/profiles');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error?.graphQLErrors?.[0]?.message || t('profiles.toast.update-failed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={t('profiles.edit.loading-title')} description={t('profiles.edit.loading-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !profile) {
    return (
      <AdminLayout title={t('profiles.edit.not-found-title')} description={t('profiles.edit.not-found-description')}>
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error ? error.message : t('profiles.edit.not-found-body')}</p>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Button asChild variant="outline">
            <Link to="/admin/profiles" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('profiles.edit.back-to-profiles')}
            </Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={t('profiles.edit.page-title')} description={t('profiles.edit.page-description')}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button asChild variant="outline">
            <Link to="/admin/profiles" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('profiles.edit.back-to-profiles')}
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 font-medium">
                {hasErrors ? tc('please-fix-errors') : tc('unsaved-changes')}
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges || hasErrors}
              className="flex items-center gap-2"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {tc('save-changes')}
            </Button>
          </div>
        </div>

        {/* Profile Settings Component */}
        <ProfileSettings
          ref={formRef}
          user={profile}
          onSave={handleProfileSettingsSave}
          onFormChange={handleFormChange}
          onLoadingStateChange={setIsSaving}
          isAdminMode={true}
          refetchProfile={async () => {
            await refetch();
          }}
        />

        {/* Public Images */}
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-4">
            {t('profiles.edit.public-images', { count: profile.images?.length || 0 })}
          </h3>
          {profile.images && profile.images.length > 0 ? (
            <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4 space-y-4">
              {profile.images.map((image: any) => (
                <div key={image.id} className="relative group break-inside-avoid mb-4">
                  <img
                    src={image.thumbnailUrl || image.optimizedUrl}
                    alt={image.title || 'Image'}
                    className="w-full rounded border hover:shadow-lg transition-shadow"
                    style={{ aspectRatio: 'auto' }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-opacity rounded flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 text-white text-xs text-center p-1">
                      {image.title || t('profiles.edit.untitled')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500">{t('profiles.edit.no-public-images')}</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
